import { useState, type ComponentType } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, Eye, EyeOff, Hourglass, Smartphone, Users, XCircle } from 'lucide-react';
import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
import { useAuthStore } from '../../stores/authStore';
import CompactCard, { type CompactFigureTone } from '../cards/CompactCard';
import { AnswerWordingModal } from './AnswerWordingModal';
import PaginationFooter from '../PaginationFooter';
import TableCardWithStates from '../TableCardWithStates';
import { SearchInput } from '../SearchInput';
import { askedFor, isFigureTask, isFillIn, taskLabel } from './bannerOptions';
import { tokensAsLabels } from './bannerTokens';
import { PAGE_SIZES, type BannerTrackerState, type TrackerColumn, type TrackerShow } from './useBannerTracker';
import type { BatchTracker, BannerToken, TrackerPerson } from './bannerApi';

interface Tile {
  show: TrackerShow;
  name: string;
  hover: string;
  icon: ComponentType<{ className?: string }>;
  tone?: CompactFigureTone;
  /** The words after the number. "people" unless it says something more. */
  unit?: string;
}

// What the server knows is that the app asked for its banners and was given this one.
const SEEN_TILES: Tile[] = [
  { show: 'seen', name: 'Seen', hover: 'Their app has fetched this banner. That does not prove they read it.', icon: Eye },
  { show: 'not_seen', name: 'Not seen', hover: 'Their app has not fetched this banner yet.', icon: EyeOff },
];

const SENT_TO: Tile = { show: 'all', name: 'Sent to', hover: 'Everyone this send went to. The list was fixed at send.', icon: Users };

/** The tiles of this send: only what it can tell, and the rare ones only when they have people. */
const tilesFor = (t: BatchTracker, counts: Record<TrackerShow, number>): Tile[] => {
  const seenTiles = t.seenTracked ? SEEN_TILES : [];
  if (!t.tracksDone) return t.seenTracked ? SEEN_TILES : [SENT_TO];
  const reached = isFigureTask(t.send.taskType);
  const fillIn = isFillIn(t.send.reachLevel);
  const olderTile: Tile = {
    show: 'older_app',
    name: 'On older app',
    hover: 'They cannot do this until they update: their app shows it as a plain banner.',
    icon: Smartphone,
    tone: 'action',
    unit: 'must update',
  };
  // Frame 8: what a consent is for, first. Pending counts everyone yet to answer, and Not seen
  // those of them it has not reached yet, each a tile of its own so each number opens its people.
  if (t.send.taskType === 'consent') {
    return [
      SENT_TO,
      { show: 'done', name: 'Agreed', hover: 'They agreed. Counts as done.', icon: CheckCircle2 },
      { show: 'declined', name: 'Declined', hover: 'They declined. The ask is over: the banner has gone for them.', icon: XCircle },
      { show: 'pending', name: 'Pending', hover: 'Not answered yet.', icon: Hourglass, tone: 'action', unit: 'to answer' },
      ...(counts.overdue > 0
        ? [{ show: 'overdue' as const, name: 'Overdue', hover: 'Not answered, and past the Complete by date.', icon: AlertTriangle, tone: 'action' as const }]
        : []),
      { show: 'not_seen', name: 'Not seen', hover: 'Their app has not fetched this banner yet.', icon: EyeOff, unit: 'not seen' },
      olderTile,
    ];
  }
  return [
    SENT_TO,
    {
      show: 'done',
      name: fillIn ? 'Filled in' : reached ? 'Reached' : 'Done',
      hover: fillIn ? 'It has a value right now.' : reached ? 'At or above their target right now.' : 'They have done it.',
      icon: CheckCircle2,
    },
    { show: 'pending', name: 'Pending', hover: 'Not done yet.', icon: Hourglass, tone: 'action' },
    ...(counts.overdue > 0
      ? [{ show: 'overdue' as const, name: 'Overdue', hover: 'Not done, and past the Complete by date.', icon: AlertTriangle, tone: 'action' as const }]
      : []),
    ...seenTiles,
    // Only where an older app cannot do it at all. Nothing else on this list needs an update.
    ...(t.needsNewApp && counts.older_app > 0 ? [olderTile] : []),
  ];
};

const GRID: Record<number, string> = { 6: 'xl:grid-cols-6', 7: 'xl:grid-cols-7' };

const TAG: Record<string, string> = {
  Done: 'bg-green-50 text-green-700 border-green-200',
  Reached: 'bg-green-50 text-green-700 border-green-200',
  'Filled in': 'bg-green-50 text-green-700 border-green-200',
  Seen: 'bg-green-50 text-green-700 border-green-200',
  Agreed: 'bg-green-50 text-green-700 border-green-200',
  Declined: 'bg-red-50 text-red-700 border-red-200',
  Pending: 'bg-amber-50 text-amber-700 border-amber-200',
  'Older app': 'bg-amber-50 text-amber-700 border-amber-200',
  Overdue: 'bg-red-50 text-red-700 border-red-200',
  'Not seen': 'bg-gray-50 text-gray-600 border-gray-200',
};

/** India time, as the rest of the header: "30 Oct 2026, 4:05 pm IST". */
const whenInIndia = (iso: string): string =>
  `${new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} IST`;

function Cell({ column, person }: { column: TrackerColumn; person: TrackerPerson }) {
  const value = column.value(person);
  if (column.tag && value != null && column.tagWhen && !column.tagWhen(value)) return <>{value}</>;
  if (column.tag) {
    return value == null ? (
      <span className="text-gray-400">—</span>
    ) : (
      <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${TAG[String(value)] ?? TAG['Not seen']}`}>
        {value}
      </span>
    );
  }
  return (
    <>
      {value == null || value === '' ? (
        <span className="text-gray-400">—</span>
      ) : column.quiet?.(person) ? (
        <span className="text-gray-400">{value}</span>
      ) : (
        value
      )}
      {column.under?.value(person) && <span className="block text-xs text-gray-500">{column.under.value(person)}</span>}
    </>
  );
}

/** Who has done one send and who has seen it: numbers first, then exactly the people behind each. */
export function BannerTracker({ state, tokens }: { state: BannerTrackerState; tokens: BannerToken[] }) {
  const [params] = useSearchParams();
  const { tracker, error, notFound, foreignProject, show, reload, query, setQuery, counts, columns, rows, pageRows } = state;
  const projects = useAuthStore((s) => s.user?.accessibleProjects);
  // Whose wording is open.
  const [wordingOf, setWordingOf] = useState<TrackerPerson | null>(null);
  const backTo = () => {
    const next = new URLSearchParams(params);
    next.delete('send');
    next.delete('show');
    return { search: `?${next.toString()}` };
  };
  const tileTo = (tile: TrackerShow, on: boolean) => {
    const next = new URLSearchParams(params);
    // Pressing the tile that is open, or the one for everyone, goes back to everyone.
    if (on || tile === 'all') next.delete('show');
    else next.set('show', tile);
    return { search: `?${next.toString()}` };
  };

  const back = (
    <Link to={backTo()} className="inline-flex flex-none items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700">
      <ArrowLeft className="h-4 w-4" />
      Sent history
    </Link>
  );

  if (error && !tracker) {
    return (
      <div className="space-y-3">
        {back}
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center text-sm text-gray-500">
          <p>{error}</p>
          {!notFound && (
            <button
              type="button"
              onClick={reload}
              className="mt-3 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }
  if (!tracker) {
    return (
      <div className="space-y-3">
        {back}
        <TableCardWithStates loading isEmpty={false} emptyMessage="">
          {null}
        </TableCardWithStates>
      </div>
    );
  }

  const { send } = tracker;
  // Not this project's: say whose it is, and show none of its people.
  if (foreignProject) {
    const name = projects?.find((p) => p.projectId === send.projectId)?.projectName;
    return (
      <div className="space-y-3">
        {back}
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center text-sm text-gray-500">
          This banner was sent on {name ?? `project ${send.projectId}`}. Switch to that project to see who has seen it.
        </div>
      </div>
    );
  }
  const tiles = tilesFor(tracker, counts);
  const state_ = send.revokedAt ? 'Removed' : send.active ? 'Live' : 'Ended';
  const asked = askedFor(send);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
        {back}
        <h2 className="min-w-0 truncate text-base font-semibold text-gray-900" title={tokensAsLabels(send.title, tokens)}>
          {tokensAsLabels(send.title, tokens)}
        </h2>
        {send.kind === 'task' ? (
          <span className="whitespace-nowrap rounded bg-primary-50 px-1.5 py-0.5 text-xs font-medium text-primary-700">
            Task · {taskLabel(send.taskType)}
          </span>
        ) : (
          <span className="whitespace-nowrap rounded border border-gray-200 bg-gray-50 px-1.5 py-px text-xs font-medium text-gray-700">
            Message
          </span>
        )}
        {asked && (
          <span className="min-w-0 truncate text-xs text-gray-600" title={asked}>
            {asked}
          </span>
        )}
        <span
          className={`inline-flex items-center rounded border px-2 py-px text-xs font-medium ${
            state_ === 'Live'
              ? 'border-green-200 bg-green-50 text-green-700'
              : state_ === 'Removed'
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-gray-200 bg-gray-50 text-gray-600'
          }`}
        >
          {state_}
        </span>
        <p className="text-xs text-gray-500 xl:ml-auto">
          {send.completeBy && `Complete by ${whenInIndia(send.completeBy)} · `}
          Sent {whenInIndia(send.createdAt)}
          {send.sentBy && ` by ${send.sentBy}`} · {tracker.people.length.toLocaleString()}{' '}
          {tracker.people.length === 1 ? 'person' : 'people'}
        </p>
      </div>

      <div className={`grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 ${GRID[tiles.length] ?? 'xl:grid-cols-5'}`}>
        {tiles.map((tile) => {
          const on = show === tile.show;
          return (
            // The app's card is a link and nothing else, so the one that is open is ringed from outside.
            <div key={tile.show} data-open={on} className={on ? 'rounded-[14px] ring-2 ring-primary-600' : undefined}>
              <CompactCard
                to={tileTo(tile.show, on)}
                icon={tile.icon}
                name={tile.name}
                hover={tile.hover}
                iconTone={tile.tone === 'action' && counts[tile.show] > 0 ? 'action' : 'plain'}
                figures={[{ value: counts[tile.show], label: tile.unit ?? (counts[tile.show] === 1 ? 'person' : 'people'), tone: tile.tone }]}
              />
            </div>
          );
        })}
      </div>

      {!tracker.seenTracked && (
        <p className="text-xs text-gray-500">Sent before Seen was kept, so this send cannot say who saw it.</p>
      )}

      {error && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {error} This is the last list read.{' '}
          <button type="button" onClick={reload} className="font-medium underline">
            Try again
          </button>
        </p>
      )}
      {tracker.figuresUnavailable && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {tracker.takesAnswer
            ? 'The answers could not be read just now, so someone who declined may show as Pending.'
            : 'The figures could not be read just now, so statuses are as last stored.'}{' '}
          <button type="button" onClick={reload} className="font-medium underline">
            Try again
          </button>
        </p>
      )}

      <div className="w-full sm:w-72">
        <SearchInput value={query} onChange={setQuery} placeholder="Search name, code or store" size="md" />
      </div>

      <TableCardWithStates
        loading={false}
        isEmpty={rows.length === 0}
        emptyMessage={query.trim() ? 'Nobody matches that search.' : 'Nobody here yet.'}
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px] text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
              <tr>
                {columns.map((c, i) => (
                  <th key={i} className="whitespace-nowrap px-4 py-3 text-left font-medium">
                    {c.header}
                  </th>
                ))}
                {tracker.takesAnswer && (
                  <th className="sticky right-0 whitespace-nowrap bg-gray-50 px-4 py-3 text-center font-medium">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pageRows.map((p) => (
                <tr key={p.empCode} className="group hover:bg-gray-50">
                  {columns.map((c, i) => (
                    <td key={i} className={`px-4 py-[11px] ${i === 0 ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                      <Cell column={c} person={p} />
                    </td>
                  ))}
                  {tracker.takesAnswer && (
                    <td className="sticky right-0 bg-white px-4 py-[11px] text-center group-hover:bg-gray-50">
                      {p.wording ? (
                        <TableActionGroup>
                          <TableActionButton
                            action="documents"
                            label="See what they were shown"
                            title="See what they were shown"
                            onClick={() => setWordingOf(p)}
                          />
                        </TableActionGroup>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TableCardWithStates>

      {rows.length > 0 && (
        <PaginationFooter
          safePage={state.safePage}
          totalPages={state.totalPages}
          pageSize={state.pageSize}
          pageSizeOptions={PAGE_SIZES}
          totalRows={rows.length}
          onPageSizeChange={state.setPageSize}
          onPrevPage={state.prevPage}
          onNextPage={state.nextPage}
        />
      )}

      {wordingOf && <AnswerWordingModal person={wordingOf} onClose={() => setWordingOf(null)} />}
    </div>
  );
}
